import slugifyWithCounter from '@sindresorhus/slugify';
import Vue from 'vue';

import PublicArticleSearch from './components/PublicArticleSearch.vue';
import TableOfContents from './components/TableOfContents.vue';
import { initializeTheme } from './portalThemeHelper.js';
import { directive as onClickaway } from 'vue-clickaway';
import { isEscape } from 'shared/helpers/KeyboardHelpers';

export const getHeadingsfromTheArticle = () => {
  const rows = [];
  const articleElement = document.getElementById('cw-article-content');
  articleElement.querySelectorAll('h1, h2, h3').forEach(element => {
    const slug = slugifyWithCounter(element.innerText);
    element.id = slug;
    element.className = 'scroll-mt-24 heading';
    element.innerHTML += `<a class="permalink text-slate-600 ml-3" href="#${slug}" title="${element.innerText}" data-turbolinks="false">#</a>`;
    rows.push({
      slug,
      title: element.innerText,
      tag: element.tagName.toLowerCase(),
    });
  });
  return rows;
};

const MIN_ZOOMABLE_SIZE = 100;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.15;
const TRANSITION_MS = 200;

const createLightboxOverlay = (imageSrc, imageAlt) => {
  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', imageAlt || 'Expanded image');
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '9999',
    cursor: 'pointer',
    opacity: '0',
    transition: `opacity ${TRANSITION_MS}ms ease`,
  });

  const closeButton = document.createElement('button');
  closeButton.innerHTML = '&times;';
  closeButton.setAttribute('aria-label', 'Close image overlay');
  Object.assign(closeButton.style, {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '32px',
    cursor: 'pointer',
    lineHeight: '1',
    padding: '4px 8px',
  });

  const img = document.createElement('img');
  img.src = imageSrc;
  img.alt = imageAlt || '';
  Object.assign(img.style, {
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain',
    cursor: 'default',
    transition: 'transform 0.1s ease',
  });

  let currentZoom = ZOOM_MIN;
  let closed = false;
  const previousOverflow = document.body.style.overflow;

  const closeLightbox = () => {
    if (closed) return;
    closed = true;
    overlay.style.opacity = '0';
    document.body.style.overflow = previousOverflow;
    setTimeout(() => overlay.remove(), TRANSITION_MS);
    document.removeEventListener('keydown', handleKeydown);
  };

  const handleKeydown = event => {
    if (isEscape(event)) closeLightbox();
  };

  // Throttle DOM writes to once per animation frame
  let rafPending = false;
  const handleWheel = event => {
    event.preventDefault();
    const direction = event.deltaY < 0 ? 1 : -1;
    currentZoom = Math.min(
      ZOOM_MAX,
      Math.max(ZOOM_MIN, currentZoom + direction * ZOOM_STEP)
    );
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(() => {
        img.style.transform = `scale(${currentZoom})`;
        rafPending = false;
      });
    }
  };

  // Close only when clicking the backdrop itself — button has its own listener
  overlay.addEventListener('click', event => {
    if (event.target === overlay) closeLightbox();
  });
  closeButton.addEventListener('click', closeLightbox);
  img.addEventListener('wheel', handleWheel, { passive: false });
  document.addEventListener('keydown', handleKeydown);

  overlay.appendChild(closeButton);
  overlay.appendChild(img);
  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';

  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });
};

// Clean up before Turbolinks caches the page: remove any open overlay
// and reset zoom state so listeners are re-attached on restore.
document.addEventListener('turbolinks:before-cache', () => {
  const overlay = document.querySelector('[role="dialog"][aria-modal="true"]');
  if (overlay) {
    overlay.remove();
    document.body.style.overflow = '';
  }
  document
    .querySelectorAll('#cw-article-content img[data-zoom-initialized]')
    .forEach(img => {
      delete img.dataset.zoomInitialized;
      delete img.dataset.zoomPending;
      img.style.cursor = '';
    });
});

export const initializeImageZoom = () => {
  const articleElement = document.getElementById('cw-article-content');
  if (!articleElement) return;

  const images = articleElement.querySelectorAll('img');
  images.forEach(img => {
    if (img.closest('a')) return;

    const setupZoom = () => {
      delete img.dataset.zoomPending;
      if (img.dataset.zoomInitialized) return;
      if (img.naturalWidth < MIN_ZOOMABLE_SIZE && img.naturalHeight < MIN_ZOOMABLE_SIZE) return;

      img.dataset.zoomInitialized = 'true';
      img.style.cursor = 'pointer';
      img.addEventListener('click', () => {
        createLightboxOverlay(img.src, img.alt);
      });
    };

    if (img.complete) {
      setupZoom();
    } else if (!img.dataset.zoomPending) {
      img.dataset.zoomPending = 'true';
      img.addEventListener('load', setupZoom, { once: true });
    }
  });
};

export const openExternalLinksInNewTab = () => {
  const { customDomain, hostURL } = window.portalConfig;
  const isSameHost =
    window.location.href.includes(customDomain) ||
    window.location.href.includes(hostURL);

  // Modify external links only on articles page
  const isOnArticlePage =
    isSameHost && document.querySelector('#cw-article-content') !== null;

  document.addEventListener('click', event => {
    if (!isOnArticlePage) return;

    // Some of the links come wrapped in strong tag through prosemirror

    const isTagAnchor = event.target.tagName === 'A';
    const isParentTagAnchor =
      event.target.tagName === 'STRONG' &&
      event.target.parentNode.tagName === 'A';

    if (isTagAnchor || isParentTagAnchor) {
      const link = isTagAnchor ? event.target : event.target.parentNode;

      const isInternalLink =
        link.hostname === window.location.hostname ||
        link.href.includes(customDomain) ||
        link.href.includes(hostURL);

      if (!isInternalLink) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer'; // Security and performance benefits
        // Prevent default if you want to stop the link from opening in the current tab
        event.stopPropagation();
      }
    }
  });
};

export const InitializationHelpers = {
  navigateToLocalePage: () => {
    const allLocaleSwitcher = document.querySelector('.locale-switcher');

    if (!allLocaleSwitcher) {
      return false;
    }

    const { portalSlug } = allLocaleSwitcher.dataset;
    allLocaleSwitcher.addEventListener('change', event => {
      window.location = `/hc/${portalSlug}/${event.target.value}/`;
    });
    return false;
  },

  initializeSearch: () => {
    const isSearchContainerAvailable = document.querySelector('#search-wrap');
    if (isSearchContainerAvailable) {
      new Vue({
        components: { PublicArticleSearch },
        directives: {
          'on-clickaway': onClickaway,
        },
        template: '<PublicArticleSearch />',
      }).$mount('#search-wrap');
    }
  },

  initializeTableOfContents: () => {
    const isOnArticlePage = document.querySelector('#cw-hc-toc');
    if (isOnArticlePage) {
      new Vue({
        components: { TableOfContents },
        data: { rows: getHeadingsfromTheArticle() },
        template: '<table-of-contents :rows="rows" />',
      }).$mount('#cw-hc-toc');
    }
  },

  appendPlainParamToURLs: () => {
    document.getElementsByTagName('a').forEach(aTagElement => {
      if (aTagElement.href && aTagElement.href.includes('/hc/')) {
        const url = new URL(aTagElement.href);
        url.searchParams.set('show_plain_layout', 'true');

        aTagElement.setAttribute('href', url);
      }
    });
  },

  initializeThemesInPortal: initializeTheme,

  initializeImageZoom,

  initialize: () => {
    openExternalLinksInNewTab();
    if (window.portalConfig.isPlainLayoutEnabled === 'true') {
      InitializationHelpers.appendPlainParamToURLs();
    } else {
      InitializationHelpers.initializeThemesInPortal();
      InitializationHelpers.navigateToLocalePage();
      InitializationHelpers.initializeSearch();
      InitializationHelpers.initializeTableOfContents();
      InitializationHelpers.initializeImageZoom();
    }
  },

  onLoad: () => {
    InitializationHelpers.initialize();
    if (window.location.hash) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      const a = document.createElement('a');
      a.href = window.location.hash;
      a['data-turbolinks'] = false;
      a.click();
    }
  },
};
